import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import api from "../../utils/axios.config";
import { baseUrl } from "../../utils/url";
import {
  Camera,
  ChevronLeft,
  ChevronRight,
  Eye,
  Calendar,
  Image,
  Loader,
  AlertCircle,
  ImageIcon,
} from "lucide-react";

// Custom Arrow Components
const CustomPrevArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
  >
    <ChevronLeft className="w-6 h-6 text-gray-700" />
  </button>
);

const CustomNextArrow = ({ onClick }) => (
  <button
    onClick={onClick}
    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
  >
    <ChevronRight className="w-6 h-6 text-gray-700" />
  </button>
);

const PhotoGallery = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleGetActivities = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get("recent-activities/get");

      if (data.success && data.data.activities.length) {
        setActivities(data?.data?.activities);
      }
    } catch (err) {
      setError("ছবি লোড করতে সমস্যা হয়েছে। পরে আবার চেষ্টা করুন।");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetActivities();
  }, []);

  const sliderSettings = {
    dots: true,
    infinite: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    touchMove: true,
    arrows: true,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    pauseOnHover: true,
    fade: true,
    cssEase: "cubic-bezier(0.25, 0.46, 0.45, 0.94)",
    dotsClass: "slick-dots custom-dots",
  };

  if (loading) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-gray-50 rounded-xl mb-10">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600">ছবি লোড হচ্ছে...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-red-50 rounded-xl mb-10">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">ত্রুটি ঘটেছে</p>
          <p className="text-red-500 text-sm">{error}</p>
          <button
            onClick={handleGetActivities}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            আবার চেষ্টা করুন
          </button>
        </div>
      </div>
    );
  }

  if (!activities || activities.length === 0) {
    return (
      <div className="min-h-96 flex items-center justify-center bg-gray-50 rounded-xl mb-10">
        <div className="text-center">
          <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">
            কোনো ছবি পাওয়া যায়নি
          </h3>
          <p className="text-gray-500">
            এই মুহূর্তে কোনো কার্যক্রমের ছবি উপলব্ধ নেই।
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-16">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
          <Camera className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-3xl font-bold text-gray-800 mb-3">
          সাম্প্রতিক কার্যক্রম
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          আমাদের বিভিন্ন সেবামূলক কার্যক্রম ও রক্তদান শিবিরের ছবি দেখুন
        </p>
      </div>

      {/* Featured Slider */}
      <div className="mb-16 relative">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <Slider {...sliderSettings}>
            {activities.map((activity, index) => (
              <div key={index} className="relative">
                <div className="relative h-96 md:h-[500px] overflow-hidden">
                  <img
                    src={`${baseUrl}${activity.imageUrl}`}
                    alt={activity.title || "কার্যক্রমের ছবি"}
                    className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                  {/* Overlay Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <div className="max-w-4xl mx-auto">
                      <h3 className="text-2xl md:text-3xl font-bold mb-3 drop-shadow-lg">
                        {activity.title || "সাম্প্রতিক কার্যক্রম"}
                      </h3>
                      <div className="flex items-center gap-4 text-white/90">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span className="text-sm">
                            {activity.date
                              ? new Date(activity.date).toLocaleDateString(
                                  "bn-BD"
                                )
                              : "তারিখ নেই"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Camera className="w-4 h-4" />
                          <span className="text-sm">ফটো গ্যালারি</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>

      {/* Gallery Grid */}
      <div className="bg-gray-50 py-12 rounded-2xl">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-800 mb-2">
              সব কার্যক্রম দেখুন
            </h3>
            <p className="text-gray-600">আমাদের সকল কার্যক্রমের ছবি একসাথে</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {activities?.map((activity) => (
              <div
                key={activity._id}
                className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              >
                {/* Image Container */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`${baseUrl}${activity.imageUrl}`}
                    alt={activity.title || "কার্যক্রমের ছবি"}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                    <button className="bg-white/90 hover:bg-white text-gray-800 px-6 py-3 rounded-full font-medium transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 flex items-center gap-2 shadow-lg">
                      <Eye className="w-4 h-4" />
                      বিস্তারিত দেখুন
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h4 className="font-semibold text-lg text-gray-800 mb-2 line-clamp-2">
                    {activity?.title || "কার্যক্রমের শিরোনাম"}
                  </h4>

                  {activity.date && (
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                      <Calendar className="w-4 h-4" />
                      <span>
                        {new Date(activity.date).toLocaleDateString("bn-BD")}
                      </span>
                    </div>
                  )}

                  <button className="w-full bg-gradient-to-r from-primary/80 to-primary hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg">
                    <Eye className="w-4 h-4" />
                    বিস্তারিত
                  </button>
                </div>

                {/* Card Border Effect */}
                <div className="absolute inset-0 rounded-xl ring-1 ring-black/5 group-hover:ring-blue-200 transition-colors duration-300" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom Styles for Slider Dots */}
      <style jsx>{`
        .custom-dots {
          bottom: 20px;
        }
        .custom-dots li button:before {
          font-size: 12px;
          color: white;
          opacity: 0.5;
        }
        .custom-dots li.slick-active button:before {
          opacity: 1;
          color: white;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default PhotoGallery;
